import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { useDictionary } from '@/lib/i18n'
import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

import { BIKE_IMAGES } from '@/constants/bike'

const AeroSection: FC = (): ReactNode => {
    const t = useDictionary()
    const { aero } = t
    const headTube = BIKE_IMAGES.headTube

    return (
        <section id='aero' aria-labelledby='aero-title' className='relative py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='grid gap-8 lg:grid-cols-[1.1fr_1fr] lg:items-end lg:gap-16'>
                    <Reveal>
                        <SectionHeading id='aero-title' eyebrow={aero.eyebrow} title={aero.title} />
                    </Reveal>

                    <Reveal delay={100}>
                        <p className='text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg lg:pb-2'>
                            {aero.intro}
                        </p>
                    </Reveal>
                </div>

                <ul className='mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
                    {aero.features.map((feature, index) => {
                        const featured = index === 0

                        return (
                            <li key={feature.id} className={cn(featured && 'md:col-span-2 lg:col-span-1 lg:row-span-2')}>
                                <Reveal delay={index * 80} className='h-full'>
                                    <article className='group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card/70 transition-colors duration-300 hover:border-race/40'>
                                        {featured && (
                                            <div className='relative aspect-[4/3] overflow-hidden bg-[#efefef] md:aspect-[21/9] lg:aspect-auto lg:min-h-[17rem] lg:flex-1'>
                                                <Image
                                                    src={headTube.src}
                                                    fill
                                                    alt={aero.imageAlt}
                                                    sizes='(min-width: 1024px) 400px, 100vw'
                                                    className='object-cover transition-transform duration-700 group-hover:scale-[1.03] motion-reduce:transition-none'
                                                />
                                            </div>
                                        )}

                                        <div className='p-6 sm:p-7'>
                                            <p className='flex items-center justify-between gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground'>
                                                <span>{feature.tag}</span>
                                                <span className='text-race-bright'>
                                                    {String(index + 1).padStart(2, '0')}
                                                </span>
                                            </p>

                                            <h3 className='display-heading mt-4 font-heading text-3xl font-semibold uppercase tracking-tight text-foreground'>
                                                {feature.title}
                                            </h3>

                                            <p className='mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base'>
                                                {feature.body}
                                            </p>
                                        </div>
                                    </article>
                                </Reveal>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}

export default AeroSection
